// ============================================================================
// TIMESHEET REOPEN MODAL
// ============================================================================
// Compact modal-md. Captures a mandatory reason and confirms with a danger
// ConfirmDialog. On reopen the backend:
//   - flips status_id from SUBMITTED back to DRAFT
//   - clears submitted_at / submitted_by
//   - stamps reopened_at / reopened_by / reopened_by_name / reopened_reason
//
// Once reopened, the timesheet can be edited by the employee or anyone in their
// team's reporting line (subject to the manage-* action scope).
//
// Mirrors the pattern used by EngagementReopenModal so users see a consistent
// reopen flow across the product.

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useReopenTimesheet } from 'hooks/api/useTimesheet';
import type { Timesheet, ReopenTimesheetRequest } from 'api/types';
import { formatDateOnly, addDaysIso } from 'utils';

interface TimesheetReopenModalProps {
  isOpen: boolean;
  timesheet: Timesheet | null;
  onClose: () => void;
  onReopened?: (updated: Timesheet) => void;
}

/**
 * Format a TIMESTAMPTZ field (submitted_at / reopened_at) as a date string
 * in the caller's local timezone. Use formatDateOnly for DATE columns
 * (week_start_date) — we deliberately split the helpers because timestamps
 * carry an instant whose local rendering depends on TZ, while DATE columns
 * have no time component and must render the same everywhere.
 */
function formatTimestamp(s?: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function formatWeekRange(weekStart?: string | null): string {
  if (!weekStart) return '—';
  const datePart = weekStart.split('T')[0];
  const end = addDaysIso(datePart, 6);
  return `${formatDateOnly(datePart)} to ${formatDateOnly(end)}`;
}

function totalLoggedFor(timesheet: Timesheet): string {
  const totalMinutes = (timesheet.entries || []).reduce(
    (sum, e) => sum + (Number(e.durationMinutes) || 0), 0);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

const TimesheetReopenModal: React.FC<TimesheetReopenModalProps> = ({
  isOpen,
  timesheet,
  onClose,
  onReopened,
}) => {
  const { reopenTimesheet, loading: saving } = useReopenTimesheet();
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reasonError, setReasonError] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setReasonError(undefined);
  }, [isOpen]);

  const handleAttempt = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('Reopen reason is required');
      return;
    }
    setReasonError(undefined);
    setConfirmOpen(true);
  };

  const handleConfirmed = async () => {
    if (!timesheet) return;
    const request: ReopenTimesheetRequest = {
      reason: reason.trim(),
      rowVersion: timesheet.rowVersion,
    };
    try {
      const updated = await reopenTimesheet(timesheet.id, request);
      setConfirmOpen(false);
      onReopened?.(updated);
      onClose();
      notificationService.toast.success(
        `Timesheet reopened (${timesheet.employeeName || 'employee'} — week of ${formatDateOnly(timesheet.weekStartDate)}).`,
      );
    } catch (err: any) {
      setConfirmOpen(false);
      const apiMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to reopen the timesheet. Please try again.';
      notificationService.toast.error(apiMsg, 6000);
    }
  };

  if (!isOpen || !timesheet) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Reopen Timesheet</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>
        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          <div
            style={{
              padding: '0.6rem 0.8rem',
              background: 'var(--bg-secondary, rgba(99,102,241,0.06))',
              borderRadius: 6,
              marginBottom: '0.8rem',
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: '0.25rem',
              columnGap: '1rem',
            }}
          >
            <span>Employee</span>
            <span style={{ textAlign: 'right' }}>
              {timesheet.employeeName || timesheet.employeeCode || '—'}
            </span>
            <span>Week</span>
            <span style={{ textAlign: 'right' }}>{formatWeekRange(timesheet.weekStartDate)}</span>
            <span>Submitted On</span>
            <span style={{ textAlign: 'right' }}>{formatTimestamp(timesheet.submittedAt)}</span>
            <span>Total Logged</span>
            <span style={{ textAlign: 'right' }}>{totalLoggedFor(timesheet)}</span>
            {timesheet.reopenedAt && (
              <>
                <span>Last Reopened</span>
                <span style={{ textAlign: 'right' }}>
                  {formatTimestamp(timesheet.reopenedAt)} by{' '}
                  <strong>{timesheet.reopenedByName || '—'}</strong>
                </span>
              </>
            )}
          </div>

          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
            Reopening will return this timesheet to <strong>DRAFT</strong> and allow the employee
            (or you, with permission) to edit entries again. Entries on closed engagements remain
            locked until those engagements are reopened first.
          </p>

          <div className="form-group">
            <label className="form-label">
              Reopen Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-input${reasonError ? ' form-field-error' : ''}`}
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(undefined); }}
              disabled={saving}
              rows={3}
              style={{ resize: 'vertical' }}
              placeholder="e.g. Activity miscoded on Thursday's entry."
            />
            {reasonError && <div className="form-error-message">{reasonError}</div>}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAttempt} disabled={saving}>
            <IconSystem.refresh size={16} /> Reopen Timesheet
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Reopen"
        message="Reopen this timesheet? It will return to DRAFT and become editable again."
        confirmLabel="Reopen"
        cancelLabel="Back"
        variant="danger"
        loading={saving}
        loadingMessage="Reopening timesheet..."
        onConfirm={handleConfirmed}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default TimesheetReopenModal;
